import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  ComplianceDocumentDefinition,
  ComplianceDocumentDefinitionRequest,
  complianceDocumentDefinitionsApi,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ComplianceDocumentDefinitionForm } from './ComplianceDocumentDefinitionForm';

interface Props extends SharedDisclosureProps {
  definition: ComplianceDocumentDefinition | null;
  folderId: number;
  onUpdated: () => void;
}

export function UpdateComplianceDocumentDefinition({
  definition,
  folderId,
  isOpen,
  onClose,
  onUpdated,
}: Props) {
  return (
    <Modal
      variant="entityDirectory"
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      {isOpen && definition && (
        <Inner
          definition={definition}
          folderId={folderId}
          onClose={onClose}
          onUpdated={onUpdated}
        />
      )}
    </Modal>
  );
}

function Inner({
  definition,
  folderId,
  onClose,
  onUpdated,
}: Omit<Props, 'definition' | 'isOpen'> & {
  definition: ComplianceDocumentDefinition;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    request: update,
    loading,
    error,
  } = useApiRequest(complianceDocumentDefinitionsApi.update);
  const defaultValues: ComplianceDocumentDefinitionRequest = {
    name: definition.name,
    description: definition.description,
    kind: definition.kind,
    type: definition.type,
    logoFileId: definition.logo?.id ?? null,
  };

  const submit = async (request: ComplianceDocumentDefinitionRequest) => {
    const response = await update(definition.id, request);
    if (response.hasError) return;

    toast({
      title: t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    onUpdated();
    onClose();
  };

  return (
    <ModalContent>
      <ModalCloseButton />
      <ModalHeader>
        {t('complianceDocumentDefinitions.editDefinition')}
        <Text
          mt={1}
          textTransform="none"
          variant="entity-directory-description"
        >
          {t('complianceDocumentDefinitions.editDescription')}
        </Text>
      </ModalHeader>
      <ComplianceDocumentDefinitionForm
        defaultValues={defaultValues}
        error={error}
        folderId={folderId}
        initialLogo={definition.logo}
        isSubmitting={loading}
        onClose={onClose}
        onSubmit={submit}
        submitLabel={t('complianceDocumentDefinitions.saveChanges')}
      />
    </ModalContent>
  );
}
